import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router';
import { getCommunicationLogEntry } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { useIsAdministrator } from '../../auth/useCallerContext';
import { CopyableText } from '../../components/CopyableText';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { CommunicationChannelChip, CommunicationOutcomeChip } from '../../components/StatusChips';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { formatBytes, formatDateTime } from '../../utils/format';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
    </Box>
  );
}

/** Text that keeps its line breaks and wraps long tokens — a stack trace, a validation list. */
function Preformatted({ children }: { children: string }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'auto',
      }}
    >
      {children}
    </Box>
  );
}

export function CommunicationLogDetailPage() {
  const { t } = useTranslation();
  const { entryGid = '' } = useParams();
  const isAdministrator = useIsAdministrator();
  const { selectedTenantGid } = useSelectedTenant();
  const hasNoTenant = !isAdministrator && selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();

  const query = useQuery({
    queryKey: queryKeys.communicationLog.detail(selectedTenantGid, entryGid),
    queryFn: ({ signal }) => getCommunicationLogEntry(entryGid, signal),
    enabled: entryGid.length > 0 && !hasNoTenant,
  });

  if (hasNoTenant) return <EmptyBlock message={t(noTenantKey)} />;
  if (query.isPending) return <LoadingBlock label={t('communication.detail.loading')} />;
  if (query.isError) return <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />;

  const entry = query.data;
  if (!entry) return null;

  // Everything that names the same exchange from the other side: the acknowledgement of this
  // message, the retries before this refusal, the status changes of the operation it became.
  const relatedSearch = entry.messageId ?? entry.externalDocumentId ?? entry.operationGid;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button component={RouterLink} to="/communication" startIcon={<ArrowBackIcon />}>
          {t('nav.communication')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {relatedSearch && (
          <Button
            component={RouterLink}
            to={`/communication?search=${encodeURIComponent(relatedSearch)}`}
            startIcon={<ManageSearchIcon />}
            variant="outlined"
          >
            {t('communication.detail.related')}
          </Button>
        )}
        {entry.operationGid && (
          <Button
            component={RouterLink}
            to={`/operations/${entry.operationGid}`}
            startIcon={<OpenInNewIcon />}
            variant="outlined"
          >
            {t('communication.detail.openOperation')}
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
            <CommunicationChannelChip channel={entry.channel} />
            <Chip
              size="small"
              variant="outlined"
              label={t(
                entry.direction === 'Inbound'
                  ? 'communication.directions.Inbound'
                  : 'communication.directions.Outbound',
              )}
            />
            <CommunicationOutcomeChip outcome={entry.outcome} />
            {entry.reasonCode && <Chip size="small" label={entry.reasonCode} />}
          </Stack>

          <Typography variant="body1" sx={{ mb: 2 }}>
            {entry.message}
          </Typography>

          <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
            <Field label={t('communication.detail.recordedAt')}>{formatDateTime(entry.recordedAt)}</Field>
            {isAdministrator && (
              <Field label={t('communication.detail.tenant')}>
                {entry.tenantName ?? t('communication.detail.noTenant')}
              </Field>
            )}
            <Field label={t('communication.detail.queue')}>{entry.queueName ?? '—'}</Field>
            <Field label={t('communication.detail.subject')}>{entry.subject ?? '—'}</Field>
            <Field label={t('communication.detail.messageId')}>
              {entry.messageId ? <CopyableText value={entry.messageId} /> : '—'}
            </Field>
            <Field label={t('communication.detail.externalDocumentId')}>
              {entry.externalDocumentId ? <CopyableText value={entry.externalDocumentId} /> : '—'}
            </Field>
            <Field label={t('communication.detail.operation')}>
              {entry.operationGid ? <CopyableText value={entry.operationGid} /> : '—'}
            </Field>
            <Field label={t('communication.detail.sender')}>
              {entry.senderParticipantId ? <CopyableText value={entry.senderParticipantId} /> : '—'}
            </Field>
            <Field label={t('communication.detail.receiver')}>
              {entry.receiverParticipantId ? <CopyableText value={entry.receiverParticipantId} /> : '—'}
            </Field>
            <Field label={t('communication.detail.deliveryCount')}>
              {entry.deliveryCount === null ? '—' : String(entry.deliveryCount)}
            </Field>
            <Field label={t('communication.detail.bodyBytes')}>
              {entry.bodyBytes === null ? '—' : formatBytes(entry.bodyBytes)}
            </Field>
            <Field label={t('communication.detail.entry')}>
              <CopyableText value={entry.entryGid} />
            </Field>
          </Stack>

          {entry.blobLocator && (
            <Box sx={{ mt: 2 }}>
              <Field label={t('communication.detail.blob')}>
                <CopyableText value={entry.blobLocator} />
              </Field>
            </Box>
          )}
        </CardContent>
      </Card>

      {entry.detail && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t('communication.detail.detailTitle')}
            </Typography>
            <Preformatted>{entry.detail}</Preformatted>
          </CardContent>
        </Card>
      )}

      {entry.exceptionType && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t('communication.detail.exceptionTitle')}
            </Typography>
            <Stack spacing={1.5}>
              <Field label={t('communication.detail.exceptionType')}>
                <CopyableText value={entry.exceptionType} />
              </Field>
              {entry.exceptionMessage && (
                <Field label={t('communication.detail.exceptionMessage')}>
                  <Preformatted>{entry.exceptionMessage}</Preformatted>
                </Field>
              )}
              {entry.exceptionStackTrace ? (
                <Field label={t('communication.detail.exceptionStack')}>
                  <Preformatted>{entry.exceptionStackTrace}</Preformatted>
                </Field>
              ) : (
                !isAdministrator && (
                  <Alert severity="info">{t('communication.detail.exceptionAdminOnly')}</Alert>
                )
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
